import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { TenantImage } from 'app/library/image-library/models/tenant-image.model';
import { ITenantImageQueryPaginated } from 'app/library/image-library/interfaces/tenant-image-paginated.query.interface';
import { IModuleImageQuery } from 'app/library/module/interfaces/module-image.interface';
import { ModuleImage } from 'app/library/module/models/module-image.model';
import { ModuleService } from 'app/library/module/services/module.service';
import { IModuleQuery } from 'app/library/module/interfaces/module.query.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { FileCommand } from 'app/projects/core/src/lib/models/file.command';
import { TenantImageUploadCommand } from 'app/projects/user/src/lib/models/tenant-image-upload.command';

export interface Pagination {
    numberOfPages: number;
    numberOfResults: number;
}

export interface ImageUploadProgress {
    fileName: string;
    percentDone: number;
    error?: HttpErrorResponse;
}

@Injectable()
export class TenantLibraryApiService {
    private apiUrl: string;

    private _tenantImages = new BehaviorSubject<TenantImage[]>([]);
    public tenantImages$ = this._tenantImages.asObservable();

    private _inProgressImages = new BehaviorSubject<ImageUploadProgress>(null);
    public inProgressImages$ = this._inProgressImages.asObservable();

    private _cancelImageUpload = new BehaviorSubject<string>(null);
    public cancelImageUpload$ = this._cancelImageUpload.asObservable();

    tenantImagesPage = 1;
    fetchingTenantImages = false;
    cancelUploadingImages = false;

    private _tenantImagesPagination: Pagination = { numberOfPages: 0, numberOfResults: 0 };

    get tenantImagesPagination(): Pagination {
        return this._tenantImagesPagination;
    }
    set tenantImagesPagination(value: Pagination) {
        this._tenantImagesPagination = value;
    }

    constructor(private http: HttpClient, public moduleFormService: ModuleService, @Inject(MAXBRAIN_ENVIRONMENT) { apiUrl }: IEnvironment) {
        this.apiUrl = apiUrl;
    }

    getTenantImages(page: number, category: string): Observable<boolean> {
        this.tenantImagesPage = page;
        return this.http.get<ITenantImageQueryPaginated>(`${this.apiUrl}/cil/tenant-library/images?page=${page}&filter=${category}`).pipe(
            map((queries) => {
                this.tenantImagesPagination = {
                    numberOfPages: queries.pagination.number_of_pages,
                    numberOfResults: queries.pagination.number_of_results,
                };
                const tenantImages = queries.data.map((query) => new TenantImage(query));
                this._tenantImages.next(tenantImages);
                return true;
            })
        );
    }

    removeTenantImages(images: { id: string }[]): Promise<any> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: {
                images,
            },
        };
        return this.http.delete(`${this.apiUrl}/cil/tenant-library/images`, options).toPromise();
    }

    uploadImage(payload: File, imageId: string): Observable<any> {
        const data = new TenantImageUploadCommand(imageId, payload).formData;
        return this.http.post<IModuleImageQuery>(`${this.apiUrl}/cil/tenant-library/upload`, data).pipe(map((query) => new ModuleImage(query)));
    }

    addModuleImage(moduleImageId: string, moduleId: string, moduleType: string): Observable<ModuleImage> {
        const data = { image: { id: moduleImageId } };
        if (moduleType === 'e_learning') {
            return this.http.put<IModuleQuery>(`${this.apiUrl}/e-learning/modules/${moduleId}`, data).pipe(map((query) => new ModuleImage(query.image)));
        } else if (moduleType === 'basic') {
            return this.http.put<IModuleQuery>(`${this.apiUrl}/modules/${moduleId}`, data).pipe(map((query) => new ModuleImage(query.image)));
        }
    }

    async uploadNewImage(payload: File): Promise<ModuleImage> {
        const data = new FileCommand('file', new File([payload], '', { type: payload.type }), payload.name).formData;
        const url = `${this.apiUrl}/cil/tenant-library/images`;

        try {
            const resp = await this._uploadFile(payload, data, url);
            if (!resp) {
                throw new Error();
            }
            this._inProgressImages.next({
                fileName: payload.name,
                percentDone: 100,
            });
            return resp;
        } catch (e) {
            this._inProgressImages.next({
                fileName: payload.name,
                percentDone: 0,
                error: e,
            });
        }
    }

    private _uploadFile(file: File, data: FormData, url: string): Promise<ModuleImage> {
        return this.http
            .request<IModuleImageQuery>(
                new HttpRequest('POST', url, data, {
                    reportProgress: true,
                })
            )
            .pipe(
                takeUntil(
                    this.cancelImageUpload$.pipe(
                        filter((fileName) => fileName === file.name),
                        take(1)
                    )
                ),
                tap((e) => {
                    if (e.type === HttpEventType.UploadProgress) {
                        const percentDone = Math.round((100 * e.loaded) / e.total);
                        if (percentDone < 100) {
                            this._inProgressImages.next({
                                fileName: file.name,
                                percentDone,
                            });
                        }
                    }
                }),
                filter((e) => e instanceof HttpResponse),
                map((e: HttpResponse<IModuleImageQuery>) => new ModuleImage(e.body))
            )
            .toPromise();
    }

    cancelUploadingImage(fileName: string): void {
        this._cancelImageUpload.next(fileName);
    }
}
